/**
 * Ask for a revision of the reader in use (#1428): a platform admin says what the next version should change and
 * answers the version's own questions, as a send-back does; the author drafts the next version from this one, which
 * stays in use until that draft is approved. RevisionFormView is pure (rendered in tests without providers);
 * RevisionSheet holds the hooks. lib/imports/revision holds the logic.
 */
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/lib/ui/button";
import { Sheet, SheetHeader } from "@/lib/ui/sheet";
import { useToast } from "@/lib/ui/toast";
import { ApiClientError } from "@/lib/api-client";
import {
  useImportExtractorVersionQuery,
  useReviseImportVersion,
  type ImportExtractor,
  type ImportExtractorVersion,
} from "@/lib/api/imports";
import { assumptionsOf, questionGroups } from "@/lib/imports/review";
import { answersBody, sentWith, type AnswerChoice, type Answers, type SendBackState } from "@/lib/imports/send-back";
import { revisionFormState } from "@/lib/imports/revision";
import { QuestionsBlock } from "./import-review";

const INPUT_CLS =
  "w-full rounded-md border border-border bg-surface px-2 py-1.5 text-xs text-on-surface focus:border-brand focus:outline-none disabled:opacity-60";

function messageOf(err: unknown, fallback: string): string {
  if (err instanceof ApiClientError) {
    const body = err.body as { error?: string; detail?: string; message?: string } | null;
    return body?.detail || body?.message || body?.error || fallback;
  }
  return err instanceof Error && err.message ? err.message : fallback;
}

/** The request: what a revision does, the note, the version's questions to answer, and the footer. */
export function RevisionFormView({
  label,
  version,
  notes,
  loading,
  note,
  answers,
  state,
  busy,
  onNote,
  onChoose,
  onSend,
  onClose,
}: {
  label: string;
  /** The version in use, which the revision starts from. */
  version: number;
  /** Its author_notes, whose questions the answers name; undefined while it loads. */
  notes: unknown;
  loading: boolean;
  note: string;
  answers: Answers;
  state: SendBackState;
  busy: boolean;
  onNote: (note: string) => void;
  onChoose: (index: number, choice: AnswerChoice | null) => void;
  onSend: () => void;
  onClose: () => void;
}) {
  const next = version + 1;
  return (
    <>
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-5 text-sm text-on-surface">
        <div className="flex flex-col gap-1.5 text-xs text-on-surface-muted">
          <p>
            The author drafts v{next} of {label} from v{version}, changing only what you ask. v{version} stays in use
            until you approve v{next}, and approving it reads the files v{version} read again with v{next}.
          </p>
          <p>
            For now a revision only adds: new columns and new streams of readings. A change to how an existing column
            is read is refused when v{next} is approved, since each imported reading is written once.
          </p>
        </div>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium">What should v{next} change?</span>
          <textarea
            className={`${INPUT_CLS} min-h-24`}
            placeholder="Say what to add, column by column (the author reads this)"
            value={note}
            disabled={busy}
            onChange={(e) => onNote(e.target.value)}
            aria-label="What the revision should change"
          />
        </label>
        <div className="flex flex-col gap-1.5">
          <h3 className="text-xs font-semibold text-on-surface">Questions v{version} asked</h3>
          {loading ? (
            <p className="text-xs text-on-surface-muted">Loading the version…</p>
          ) : (
            <QuestionsBlock
              groups={questionGroups(notes)}
              assumptions={assumptionsOf(notes)}
              answering={{ choices: answers, onChoose, locked: busy, lead: "If you ask for a revision:" }}
            />
          )}
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-border p-4">
        {!state.ok && (note.trim() || state.blankCorrection) && (
          <span className="text-[11px] text-warning">{state.reason}</span>
        )}
        <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
          Close
        </Button>
        <Button size="sm" disabled={!state.ok || busy || loading} aria-label="Ask for the revision" onClick={onSend}>
          {busy && <Loader2 className="mr-1.5 size-3.5 animate-spin" />}
          Ask for the revision
        </Button>
      </div>
    </>
  );
}

export function RevisionSheet({
  extractor,
  version,
  onClose,
}: {
  extractor: ImportExtractor;
  version: ImportExtractorVersion;
  onClose: () => void;
}) {
  const detail = useImportExtractorVersionQuery(extractor.id, version.version);
  const revise = useReviseImportVersion();
  const toast = useToast();
  const [note, setNote] = useState("");
  const [answers, setAnswers] = useState<Record<number, AnswerChoice>>({});
  const label = extractor.label || extractor.key;
  const state = revisionFormState(note, answers);

  const onSend = async () => {
    if (!state.ok) return;
    try {
      await revise.mutateAsync({
        extractorId: extractor.id,
        version: version.version,
        note: note.trim(),
        answers: answersBody(answers),
      });
      toast.push("ok", `A revision of ${label} v${version.version} was asked, with ${sentWith(note, answers)}.`);
      onClose();
    } catch (err) {
      toast.push("error", messageOf(err, "Couldn't ask for the revision."));
    }
  };

  return (
    <Sheet open onClose={onClose} widthClass="max-w-2xl" ariaLabel={`Ask for a revision of ${label} v${version.version}`}>
      <SheetHeader title={`Ask for a revision of ${label} v${version.version}`} subtitle={extractor.key} onClose={onClose} />
      <RevisionFormView
        label={label}
        version={version.version}
        notes={detail.data?.author_notes}
        loading={detail.isLoading}
        note={note}
        answers={answers}
        state={state}
        busy={revise.isPending}
        onNote={setNote}
        onChoose={(i, c) =>
          setAnswers((cur) => {
            const next = { ...cur };
            if (c === null) delete next[i];
            else next[i] = c;
            return next;
          })
        }
        onSend={() => void onSend()}
        onClose={onClose}
      />
    </Sheet>
  );
}
